import { AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Receipt status badge — three visual states.
 *
 *   scan_status: "completed" | "pending_retry" | "needs_review" | "failed"
 *
 * Compact mode hides the completed indicator (useful in dense lists where
 * the absence of a badge implies "completed").
 */
export default function ReceiptStatusBadge({ status, compact = false, size = "sm" }) {
    const isProcessing = status === "pending_retry" || status === "needs_review";
    const isFailed = status === "failed";
    const isCompleted = !status || status === "completed";

    // Size variants
    const fontSize = size === "xs" ? 10 : 11;
    const dotSize = size === "xs" ? 6 : 8;
    const iconSize = size === "xs" ? 11 : 13;
    const padding = size === "xs" ? "2px 6px" : "3px 8px";

    // ── COMPLETED ────────────────────────────────────────────────────────────
    if (isCompleted) {
        if (compact) return null;
        return (
            <span
                className="inline-flex items-center"
                style={{ color: "#10B981" }}
                title="Completed"
                data-testid="status-badge-completed"
            >
                <CheckCircle2 style={{ width: iconSize + 2, height: iconSize + 2 }} />
            </span>
        );
    }

    // ── COULDN'T READ ────────────────────────────────────────────────────────
    if (isFailed) {
        if (compact) return null;
        return (
            <span
                className="inline-flex items-center gap-1 rounded-full"
                style={{
                    backgroundColor: "#FEF2F2",
                    color: "#DC2626",
                    fontSize,
                    fontWeight: 600,
                    padding,
                    fontFamily: "DM Sans, sans-serif",
                    border: "1px solid #FECACA",
                    whiteSpace: "nowrap",
                }}
                data-testid="status-badge-failed"
            >
                <AlertTriangle style={{ width: iconSize, height: iconSize }} strokeWidth={2.2} />
                Couldn't read
            </span>
        );
    }

    // ── PROCESSING (pending_retry + needs_review) ─────────────────────────────
    return (
        <>
            <style>{`
        @keyframes mb-pulse-ping {
          0%   { transform: scale(1);   opacity: 0.65; }
          80%  { transform: scale(2.2); opacity: 0;    }
          100% { transform: scale(2.2); opacity: 0;    }
        }
      `}</style>
            <span
                className="inline-flex items-center gap-1.5 rounded-full"
                style={{
                    backgroundColor: "#EFF6FF",
                    color: "#1D4ED8",
                    fontSize,
                    fontWeight: 600,
                    padding,
                    fontFamily: "DM Sans, sans-serif",
                    border: "1px solid #BFDBFE",
                    whiteSpace: "nowrap",
                }}
                data-testid="status-badge-processing"
            >
                <span
                    className="relative inline-flex flex-shrink-0"
                    style={{ width: dotSize, height: dotSize }}
                >
                    <span
                        className="absolute inline-flex rounded-full"
                        style={{
                            width: dotSize,
                            height: dotSize,
                            backgroundColor: "#2D5BFF",
                            animation: "mb-pulse-ping 1.8s cubic-bezier(0,0,0.2,1) infinite",
                        }}
                    />
                    <span
                        className="relative inline-flex rounded-full"
                        style={{ width: dotSize, height: dotSize, backgroundColor: "#2D5BFF" }}
                    />
                </span>
                Processing…
            </span>
        </>
    );
}

/**
 * Helper: determine the right tap behaviour given a receipt's status.
 * Returns one of: "open" | "processing-toast" | "couldnt-read-modal"
 */
export function getReceiptTapAction(scan_status) {
    if (scan_status === "pending_retry" || scan_status === "needs_review") {
        return "processing-toast";
    }
    if (scan_status === "failed") {
        return "couldnt-read-modal";
    }
    return "open";
}